/**
 * The import page's reasoning (#28 v2, docs/list-standard.md §10), kept apart from the drawing so
 * each rule is tested on its own: how a header is compared, which field a header suggests, how the
 * person's choices become the settings «Test» sends, how a saved template lands on another file,
 * when «Import» may be pressed, and the file handed back with an error column.
 *
 * Nothing here judges a row. The route does, and this only carries what it said.
 */
import { foldArabic } from "./arabic.js";
import { csvOf } from "./export.js";
/** A word the route sent in both languages, in the reader's; a plain string (v1) is the same in both. */
export function said(words, i18n) {
    if (!words)
        return '';
    if (typeof words === 'string')
        return words;
    return (i18n.locale === 'ar' ? words.ar : words.en) || words.en || words.ar || '';
}
/** The words a choice is said by: its first label, else its value. */
export function choiceWords(c) {
    return c.labels?.[0] ?? c.value;
}
/**
 * A header as it is compared (§10.3): case and runs of spaces ignored, the hamza forms of alef read
 * as alef, ta marbuta as ha, alef maqsura as ya, tashkeel and tatweel dropped, Arabic-Indic digits
 * read as digits. Punctuation stays: «E-mail» is matched because a column names it as an alias.
 */
export function normaliseHeader(text) {
    return foldArabic(text).replace(/\s+/g, ' ').trim();
}
/** Every name a header may carry for this field: its two headers, its aliases, its key. */
function namesOf(c) {
    const h = typeof c.header === 'string' ? [c.header] : [c.header.ar, c.header.en];
    return new Set([...h, ...(c.aliases ?? []), c.key].filter(Boolean).map(normaliseHeader));
}
/** A `ref` field is chosen by one of its lookups; its first is what a header alone suggests. */
function targetFor(c) {
    return c.type === 'ref' && c.lookups?.length ? { column: c.key, lookup: c.lookups[0].key } : { column: c.key };
}
/**
 * The mapping a file's headers suggest, by header, alias and key, keyed by each file column's
 * position. A field takes one file column: the second header naming it is left unmapped, as is a
 * header nothing names. (The route suggests on upload; this is for another sheet chosen on the page.)
 */
export function suggestMapping(headers, importer) {
    const names = importer.columns.map((c) => ({ c, names: namesOf(c) }));
    const used = new Set();
    const out = {};
    for (const h of headers) {
        const n = normaliseHeader(h.header);
        const hit = n ? names.find((x) => !used.has(x.c.key) && x.names.has(n)) : undefined;
        if (hit)
            used.add(hit.c.key);
        out[String(h.index)] = hit ? targetFor(hit.c) : null;
    }
    return out;
}
/** A sheet's header row by position — the gaps the upload skipped kept empty — as a saved template keeps it. */
export function headerRow(sheet) {
    const width = Math.max(0, ...sheet.headers.map((h) => h.index + 1));
    const out = Array.from({ length: width }, () => '');
    for (const h of sheet.headers)
        out[h.index] = h.header;
    return out;
}
/** A target as the field select holds it: `key`, or `key:lookup` for a `ref` field. */
export function targetValue(t) {
    return !t ? '' : t.lookup ? `${t.column}:${t.lookup}` : t.column;
}
export function targetOf(value) {
    if (typeof value !== 'string' || !value)
        return null;
    const at = value.indexOf(':');
    return at < 0 ? { column: value } : { column: value.slice(0, at), lookup: value.slice(at + 1) };
}
/**
 * The fields a file column may be mapped to, as the select offers them: grouped by the record they
 * belong to (`own` names the list's own); a `ref` field once per lookup, «Department / Name»; a
 * field of another record «Person / Email», its group carrying the unit that keeps it.
 */
export function fieldOptions(importer, i18n, own) {
    const out = [];
    for (const c of importer.columns) {
        const header = said(c.header, i18n);
        const record = said(c.record, i18n);
        const unit = said(c.unit, i18n);
        const group = record ? (unit ? `${record} · ${unit}` : record) : own;
        const help = said(c.help, i18n) || undefined;
        if (c.type === 'ref' && c.lookups?.length) {
            for (const l of c.lookups) {
                out.push({ value: `${c.key}:${l.key}`, label: `${header} / ${said(l.label, i18n)}`, group, ...(help ? { detail: help } : {}) });
            }
        }
        else {
            out.push({ value: c.key, label: record ? `${record} / ${header}` : header, group, ...(help ? { detail: help } : {}) });
        }
    }
    // Grouped as the select draws them: the list's own fields first, then each other record.
    const order = [...new Set([own, ...out.map((o) => o.group)])];
    return out.sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));
}
export const IMPORT_DEFAULTS = { header_row: 1, date_format: 'auto', decimal: '.', thousands: ',', mode: 'create' };
/** The page's state for a sheet, from settings (a suggestion, a template, the last test): every file column named, unmapped ones null. */
export function initialImportState(sheet, settings) {
    const header = settings.header_row ?? IMPORT_DEFAULTS.header_row;
    const mapping = {};
    for (const h of sheet.headers)
        mapping[String(h.index)] = settings.mapping[String(h.index)] ?? null;
    return {
        sheet: sheet.name,
        header_row: header,
        start_row: settings.start_row ?? header + 1,
        date_format: settings.date_format ?? IMPORT_DEFAULTS.date_format,
        decimal: settings.decimal ?? IMPORT_DEFAULTS.decimal,
        thousands: settings.thousands ?? IMPORT_DEFAULTS.thousands,
        mode: settings.mode ?? IMPORT_DEFAULTS.mode,
        mapping,
        constants: { ...(settings.constants ?? {}) },
        translations: Object.fromEntries(Object.entries(settings.translations ?? {}).map(([k, v]) => [k, { ...v }])),
    };
}
/**
 * What one choice on the page does. A field takes one file column, so mapping it lets go of the
 * column that held it; a field carried by a column needs no constant, so mapping it drops one.
 */
export function importReducer(state, action) {
    switch (action.type) {
        case 'load':
            return initialImportState(action.sheet, action.settings);
        case 'map': {
            const mapping = { ...state.mapping };
            const constants = { ...state.constants };
            if (action.target) {
                for (const [p, t] of Object.entries(mapping))
                    if (t?.column === action.target.column)
                        mapping[p] = null;
                delete constants[action.target.column];
            }
            mapping[String(action.position)] = action.target;
            return { ...state, mapping, constants };
        }
        case 'constant': {
            const constants = { ...state.constants };
            if (action.value === null || action.value === '')
                delete constants[action.column];
            else
                constants[action.column] = action.value;
            return { ...state, constants };
        }
        case 'translate': {
            const translations = { ...state.translations, [action.column]: { ...(state.translations[action.column] ?? {}), [action.from]: action.to } };
            return { ...state, translations };
        }
        case 'option': {
            const next = { ...state, [action.key]: action.value };
            // The rows start under the header: moving the header down moves the first row with it.
            if (action.key === 'header_row' && next.start_row <= Number(action.value))
                next.start_row = Number(action.value) + 1;
            return next;
        }
    }
}
/** The settings «Test» sends (§10.3), from what the page holds. */
export function settingsOf(state) {
    const mapped = new Set(Object.values(state.mapping).filter((t) => Boolean(t)).map((t) => t.column));
    const constants = Object.fromEntries(Object.entries(state.constants).filter(([k, v]) => v !== '' && !mapped.has(k)));
    const translations = Object.fromEntries(Object.entries(state.translations).filter(([, v]) => Object.keys(v).length));
    return {
        sheet: state.sheet,
        header_row: state.header_row,
        start_row: state.start_row,
        date_format: state.date_format,
        decimal: state.decimal,
        thousands: state.thousands,
        mode: state.mode,
        mapping: { ...state.mapping },
        constants,
        translations,
    };
}
/** The same settings, written one way: key order and absent fields do not make two settings differ. */
function canonical(v) {
    if (Array.isArray(v))
        return `[${v.map(canonical).join(',')}]`;
    if (v && typeof v === 'object') {
        return `{${Object.keys(v).filter((k) => v[k] !== undefined).sort()
            .map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
    }
    return JSON.stringify(v ?? null);
}
/**
 * Whether «Import» may be pressed — the one rule of the pattern: the file was tested, nothing was
 * refused, it was not applied already, and the page still holds the settings it sent to that test
 * (`sent`). A mapping changed after «Test» is a file nobody has checked.
 */
export function canApply(review, sent, state) {
    if (!review || !sent || review.status !== 'reviewing' || review.error_count !== 0 || !review.settings)
        return false;
    return canonical(settingsOf(state)) === canonical(sent);
}
/**
 * A route's answer read the one way: the library's outcome (`{state: 'staged', …}`,
 * `{state: 'applied', review}`, `{state: 'saved', template}`) or the thing itself; and a refusal
 * answered in the body (`{state: 'refused', problem}`) is a refusal, as a 4xx with problems is.
 */
export function answerOf(kind, body) {
    const b = (body ?? {});
    if (b['state'] === 'refused' && b['problem'])
        return { refused: b['problem'] };
    if (kind === 'applied' && b['state'] === 'applied' && b['review'])
        return { ok: b['review'] };
    if (kind === 'saved' && b['state'] === 'saved' && b['template'])
        return { ok: b['template'] };
    return { ok: body };
}
/** Where the suggested mapping came from, in words — a template by its name, never by its id (§10.3). */
export function suggestionNote(suggestion, sheet) {
    if (suggestion.source === 'template' && suggestion.template?.name)
        return { key: 'list.import.suggested.template', params: { name: suggestion.template.name } };
    if (suggestion.source === 'remembered')
        return { key: 'list.import.suggested.last' };
    const named = sheet.headers.filter((h) => h.header.trim());
    const mapped = named.filter((h) => suggestion.settings.mapping[String(h.index)]).length;
    return { key: 'list.import.suggested.headers', params: { mapped, total: named.length } };
}
/**
 * A saved template on another file, by header (§10.3): a header it knew maps as it did; a header it
 * did not know is left unmapped and named (`fresh`); a header it expected and the file lacks is
 * named too (`missing`). The sheet is the file's own.
 */
export function applyTemplate(saved, sheet) {
    const known = new Map();
    saved.headers.forEach((h, j) => { const n = normaliseHeader(h); if (n && !known.has(n))
        known.set(n, j); });
    const used = new Set();
    const mapping = {};
    const fresh = [];
    for (const h of sheet.headers) {
        mapping[String(h.index)] = null;
        if (!h.header.trim())
            continue;
        const j = known.get(normaliseHeader(h.header));
        if (j === undefined || used.has(j)) {
            fresh.push(h.header);
            continue;
        }
        used.add(j);
        const m = saved.settings.mapping[String(j)];
        mapping[String(h.index)] = m ? { ...m } : null;
    }
    const missing = saved.headers.filter((h, j) => h.trim() && !used.has(j));
    return { settings: { ...saved.settings, sheet: sheet.name, mapping }, fresh, missing };
}
/** A problem in the reader's words: the catalogue's by its key, else the route's sentence; then «did you mean …?». */
export function problemText(p, i18n) {
    const params = { ...(p.params ?? {}), ...(p.header ? { header: p.header } : {}) };
    const words = i18n.t(p.key, params);
    const base = words === p.key ? p.message : words;
    return p.suggestion ? `${base} ${i18n.t('list.import.didYouMean', { value: p.suggestion })}` : base;
}
/**
 * The problems said once per kind, column and value, with their lines: «no department is called
 * «التشغيل» — lines 57, 58, 91». The whole file's first, then by the first line each was found on.
 */
export function groupProblems(problems) {
    const groups = new Map();
    for (const p of problems) {
        const id = [p.key, p.file_column ?? p.column ?? '', canonical(p.params ?? {}), p.suggestion ?? ''].join('|');
        const g = groups.get(id);
        if (g) {
            if (!g.lines.includes(p.line))
                g.lines.push(p.line);
        }
        else
            groups.set(id, { problem: p, lines: [p.line] });
    }
    for (const g of groups.values())
        g.lines.sort((a, b) => a - b);
    return [...groups.values()].sort((a, b) => a.lines[0] - b.lines[0]);
}
/**
 * The file handed back (§10.4): the file's own rows with an «error» column appended, every refused
 * line's problems in the reader's words — to be corrected and uploaded again; the problems of the
 * whole file after the column's name. Null when the route did not answer the rows, so nothing is
 * offered rather than an empty file.
 */
export function reportCsv(tested, i18n) {
    if (!tested.rows?.length)
        return null;
    const byLine = new Map();
    for (const p of tested.problems) {
        const line = p.line > 0 ? p.line : 0;
        byLine.set(line, [...(byLine.get(line) ?? []), problemText(p, i18n)]);
    }
    const header = headerRow({ headers: tested.headers });
    const width = Math.max(header.length, ...tested.rows.map((r) => r.cells.length));
    const pad = (cells) => Array.from({ length: width }, (_, i) => cells[i] ?? '');
    const whole = byLine.get(0) ?? [];
    const error = i18n.t('list.import.errorColumn');
    return csvOf({
        columns: [...pad(header), whole.length ? `${error} — ${whole.join(' · ')}` : error].map((label, i) => ({ key: String(i), label, kind: 'text' })),
        rows: tested.rows.map((r) => [...pad(r.cells), (byLine.get(r.line) ?? []).join(' · ')].map((text) => ({ text, raw: text }))),
    });
}
/** What saving a template keeps, said before it is saved — and never a row or the file (the page says that too). */
export function templateSummary(settings, importer, sheet, i18n) {
    const t = i18n.t.bind(i18n);
    const sep = i18n.locale === 'ar' ? '، ' : ', ';
    const column = (key) => importer.columns.find((c) => c.key === key);
    const name = (key) => said(column(key)?.header, i18n) || key;
    const mapped = Object.values(settings.mapping).filter(Boolean).length;
    const ignored = sheet.headers.filter((h) => h.header.trim() && !settings.mapping[String(h.index)]).map((h) => `«${h.header}»`);
    const out = [ignored.length
            ? t('list.import.summary.mappingIgnoring', { n: mapped, columns: ignored.join(sep) })
            : t('list.import.summary.mapping', { n: mapped })];
    out.push(t('list.import.summary.options', { sheet: settings.sheet ?? sheet.name, date: dayFormatWords(settings.date_format ?? IMPORT_DEFAULTS.date_format, i18n), row: settings.start_row ?? 2 }));
    for (const [key, value] of Object.entries(settings.constants ?? {})) {
        const choice = column(key)?.choices?.find((c) => c.value === value);
        out.push(t('list.import.summary.constant', { field: name(key), value: choice ? choiceWords(choice) : value }));
    }
    for (const pairs of Object.values(settings.translations ?? {})) {
        for (const [from, to] of Object.entries(pairs))
            out.push(t('list.import.summary.translation', { from, to }));
    }
    out.push(settings.mode === 'upsert' && importer.update_key
        ? t('list.import.summary.mode.upsert', { key: name(importer.update_key) })
        : t('list.import.summary.mode.create'));
    return out;
}
/**
 * The template file's columns (NEW-09): each header exactly as the route reads it, whether it is
 * required, what it wants, the words of a known set (its drop-down) and an example.
 */
export function templateColumns(importer, i18n) {
    return importer.columns.map((c) => {
        const help = said(c.help, i18n);
        const example = said(c.example, i18n);
        return {
            header: said(c.header, i18n),
            ...(c.required ? { required: true } : {}),
            ...(c.type === 'choice' && c.choices?.length ? { choices: c.choices.map(choiceWords) } : {}),
            ...(help ? { help } : {}),
            ...(example ? { example } : {}),
        };
    });
}
/** The instructions sheet's words, in the reader's language. */
export function templateGuide(i18n) {
    const t = i18n.t.bind(i18n);
    return {
        sheet: t('list.import.guide.sheet'),
        lede: t('list.import.guide.lede'),
        heads: { column: t('list.import.guide.column'), required: t('list.import.guide.required'), what: t('list.import.guide.what'), values: t('list.import.guide.values'), example: t('list.import.guide.example') },
        yes: t('value.yes'),
        no: t('value.no'),
        requiredPrompt: t('list.import.guide.requiredPrompt'),
    };
}
/** The day formats the page offers, «detect» first: it reads ISO days, date cells and each unambiguous day/month pair (core-rnqmwcjd). */
export const DATE_FORMATS = ['auto', 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD-MM-YYYY', 'YYYY/MM/DD', 'DD.MM.YYYY'];
/** A day format as the reader reads it: «detect» in words, a pattern as written. */
export function dayFormatWords(format, i18n) {
    return format === 'auto' ? i18n.t('list.import.date.auto') : format;
}
/**
 * A sample cell as the mapping table shows it. A cell a spreadsheet typed as a date holds the day's
 * number (32994), and the server reads it as the day it is whatever the format: in a column mapped to
 * a date field the sample says that day (01/05/1990), not the number. Anything else is as written.
 */
export function sampleWords(value, column) {
    if (column?.type !== 'date' || !/^\d{2,6}(\.0+)?$/.test(value.trim()))
        return value;
    const n = Math.floor(Number(value));
    if (n < 61 || n > 2958465)
        return value;
    const iso = new Date(Date.UTC(1899, 11, 30) + n * 86_400_000).toISOString().slice(0, 10);
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}
/** The line numbers of a problem, as the reader counts them: «line 14», «lines 57، 58، 91», the first few and how many more. */
export function linesText(lines, i18n, shown = 5) {
    const real = lines.filter((n) => n > 0);
    if (!real.length)
        return i18n.t('list.import.wholeFile');
    const sep = i18n.locale === 'ar' ? '، ' : ', ';
    const head = real.slice(0, shown).join(sep);
    const more = real.length - shown;
    if (real.length === 1)
        return i18n.t('list.import.lines.one', { line: head });
    return more > 0 ? i18n.t('list.import.lines.more', { lines: head, n: more }) : i18n.t('list.import.lines.many', { lines: head });
}
//# sourceMappingURL=import-model.js.map